import Image from 'next/image'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import xss from 'xss'

import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { Separator } from '@wartek-id/separator'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { CheckboxGroup, Checkbox } from '@wartek-id/checkbox-v1'

import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { Header } from 'app/KinerjaShared/components/Header'
import { usePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'app/KinerjaPSPegawaiPage/components/Form'
import { ButtonPelaksanaanAppealRollback } from 'app/KinerjaPSPegawaiPage/components/ButtonPelaksanaanAppealRollback'
import { nl2br, stripHTMLTags } from 'utils/text'
import { InternalServerError } from 'utils/customError'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
import usePPSPeriodGuard from 'app/KinerjaPSPegawaiPage/hooks/usePPSPeriodGuard'
import LoadingSpinner from '../components/LoadingSpinner'

import type {
  TPraktikKinerjaUpayaTindakLanjutFormValue,
  TPraktikKinerjaRefleksiTindakLanjutFormValue,
} from 'types/kinerja-ps-pegawai'
import type { SubmitHandler } from 'react-hook-form'

const DialogConfirmation = ({
  onBack,
  onConfirm,
  isSubmitting = false,
  ...dialogProps
}: Omit<DialogProps, 'children'> & {
  isSubmitting?: boolean
  onBack?: () => void
  onConfirm: () => void
}) => {
  const [checked, setChecked] = useState([])
  const isConfirmed = checked.filter((item) => Boolean(item)).length === 2

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <div className="p-6">
          <div className="mb-6">
            <Text variant="heading-md" className="mb-5">
              Saya menyatakan sudah:
            </Text>

            <CheckboxGroup
              name="refleksi-tindak-lanjut-confirmation"
              onChange={setChecked}
              value={checked}
              className="flex flex-col gap-2"
            >
              <Checkbox value="menyelesaikan" labelPosition="center">
                <Text>
                  Mengetahui Tim Kinerja akan memberikan hasil pemantauan
                  berdasarkan pengisian
                </Text>
              </Checkbox>
              <Checkbox value="mengetahui" labelPosition="center">
                <Text>
                  Mengetahui Pejabat Penilai Kinerja akan finalisasi saat
                  Penetapan Predikat Kinerja
                </Text>
              </Checkbox>
            </CheckboxGroup>
          </div>

          <div className="flex gap-3">
            <Button size="lg" color="white" onClick={onBack}>
              Kembali
            </Button>
            <Button
              size="lg"
              color="blue"
              onClick={onConfirm}
              isLoading={isSubmitting}
              disabled={!isConfirmed || isSubmitting}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const MSG_ERR_REQUIRED = 'Wajib diisi'
const VIEW_ONLY_LABELS = {
  inspirasi: 'Satu inspirasi yang Anda dapatkan:',
  perubahan_praktik:
    'Satu perubahan praktik yang ingin Anda lakukan di satuan pendidikan:',
  tantangan:
    'Tiga tantangan paling sulit dalam melakukan perubahan praktik tersebut:',
  rencana:
    'Rencana Anda dalam mengatasi tantangan tersebut sehingga perubahan praktik dapat terjadi:',
}
const FORM_LABELS = {
  inspirasi: {
    title: 'Upaya Belajar',
    label: 'Sebutkan minimal 1 inspirasi yang Anda dapatkan:',
  },
  perubahan_praktik: {
    title: null,
    label:
      'Sebutkan minimal 1 perubahan praktik yang ingin Anda lakukan di satuan pendidikan:',
  },
  tantangan: {
    title: 'Upaya Refleksi',
    label:
      'Sebutkan minimal 3 tantangan paling sulit dalam melakukan perubahan praktik tersebut:',
  },
  rencana: {
    title: 'Perubahan Praktik',
    label:
      'Jelaskan rencana Anda dalam mengatasi tantangan tersebut sehingga perubahan praktik dapat terjadi:',
  },
}

export default function PSPelaksanaanUpayaTindakLanjutPage() {
  const toast = useToast()
  const router = useRouter()
  const { trackEvent } = usePPSTrackEvent()
  const { isPeriodSuccess, pelaksanaanGuard } = usePPSPeriodGuard()
  const [isPageLoading, setPageLoading] = useState(true)

  const toggle = useToggleSdkContext()
  const appealRollbackEnabled =
    toggle?.['kinerja-ps-pegawai-appeal-rollback-enabled'] || false

  const form = useForm<TPraktikKinerjaRefleksiTindakLanjutFormValue>({
    defaultValues: {
      inspirasi: '',
      perubahan_praktik: '',
      tantangan: '',
      rencana: '',
    },
  })

  // Fetch "Upaya Tindak Lanjut" form data
  const {
    isLoading: isUpayaTindakLanjutLoading,
    formStatus: upayaTindakLanjutFormStatus,
    parsedFormValues: parsedUpayaTindakLanjutValues,
  } = usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaUpayaTindakLanjutFormValue>(
    {
      formName: 'upaya_tindak_lanjut',
    }
  )

  // Fetch "Refleksi Tindak Lanjut" form data
  const {
    isLoading,
    isSuccess,
    isViewOnly,
    isAppeal,
    isLocked,
    parsedFormValues,
    submitFormValues,
    submitStatus,
    psSummaryHookResult,
  } = usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaRefleksiTindakLanjutFormValue>(
    {
      formName: 'refleksi_tindak_lanjut',
    }
  )

  const isSubmitDisabled = submitStatus === 'loading' || !form.formState.isDirty

  useEffect(() => {
    if (isSuccess) {
      Object.entries(parsedFormValues).forEach(([name, value]: any) =>
        form.setValue(name, value, {
          shouldDirty: true,
        })
      )
    }
  }, [form, parsedFormValues, isSuccess])

  useEffect(() => {
    if (
      psSummaryHookResult.summaryFetchStatus === 'success' &&
      !isLoading &&
      !isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (isSuccess) {
      if (!psSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
      } else if (isLocked) {
        router.replace(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isLocked,
    isSuccess,
    psSummaryHookResult.isPelaksanaanState,
    psSummaryHookResult.summaryFetchStatus,
  ])

  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)
  const onSubmit: SubmitHandler<
    TPraktikKinerjaRefleksiTindakLanjutFormValue
  > = () => {
    setIsDialogConfirmationOpen(true)
  }

  const handleSubmitFormValues = async (saveAsDraft = true) => {
    try {
      await submitFormValues({ values: form.getValues(), saveAsDraft })

      trackEvent('pelaksanaan pegawai', 'submit form E')

      toast({
        message: saveAsDraft ? 'Berhasil simpan draf!' : 'Berhasil kirim!',
        type: 'success',
      })

      if (window.history.state.idx <= 1) {
        router.push(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        )
      } else {
        router.back()
      }
    } catch (e) {
      console.error(e)
      toast({
        message: saveAsDraft
          ? 'Gagal simpan draf. Coba lagi.'
          : 'Gagal kirim. Coba lagi.',
      })
    } finally {
      setIsDialogConfirmationOpen(false)
    }
  }

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
    } else if (
      isPeriodSuccess &&
      upayaTindakLanjutFormStatus === 'success' &&
      isSuccess
    ) {
      setPageLoading(false)
    }
  }, [
    isPeriodSuccess,
    pelaksanaanGuard.isOpen,
    router,
    upayaTindakLanjutFormStatus,
    isSuccess,
  ])

  if (isPageLoading) {
    return (
      <div className="px-3 mx-auto max-w-7xl py-9">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="px-3 mx-auto max-w-7xl py-9">
      <Header
        title={
          <div className="flex items-center gap-x-3">
            Refleksi Tindak Lanjut
            {isViewOnly && !isAppeal && (
              <Badge variant="success">Selesai</Badge>
            )}
            {isAppeal && (
              <Badge variant="neutral">Perubahan sedang diajukan</Badge>
            )}
          </div>
        }
        className="mb-6"
      />

      {isLoading && (
        <div
          data-testid="loading-spinner-upaya-tindak-lanjut"
          className="flex justify-center w-full p-6"
        >
          <Spinner />
        </div>
      )}

      {isSuccess && (
        <>
          {isViewOnly ? (
            <div className="max-w-4xl px-3 mx-auto py-9">
              <SimpleCard className="flex-1 !rounded mb-24">
                {isSuccess && (
                  <>
                    <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
                      <Text variant="heading-lg">
                        Pengisian Refleksi dan Evaluasi Tindak Lanjut
                      </Text>
                    </div>

                    <div className="p-6 space-y-6 bg-white rounded-b">
                      {Object.entries(VIEW_ONLY_LABELS).map(
                        ([key, label], index) => (
                          <>
                            <div
                              key={`refleksi-tindak-lanjut-view-only-${key}`}
                              className="space-y-2 bg-white rounded-b"
                            >
                              <div className="flex items-center gap-x-3">
                                <div className="flex items-center justify-center w-6 h-6 text-white rounded-full bg-blue-70">
                                  <Text color="inverse" variant="body-sm-bold">
                                    {index + 1}
                                  </Text>
                                </div>
                                <Text variant="action">{label}</Text>
                              </div>
                              <div className="pl-9">
                                <Text
                                  color="subdued"
                                  dangerouslySetInnerHTML={{
                                    __html: xss(
                                      nl2br(
                                        stripHTMLTags(
                                          parsedFormValues?.[key] ?? '-'
                                        )
                                      )
                                    ),
                                  }}
                                />
                              </div>
                            </div>
                            <Separator orientation="horizontal" />
                          </>
                        )
                      )}
                      {appealRollbackEnabled && (
                        <div className="flex justify-end">
                          <ButtonPelaksanaanAppealRollback formName="refleksi_tindak_lanjut" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </SimpleCard>
            </div>
          ) : (
            <>
              <div className="flex gap-6">
                <SimpleCard className="p-5 space-y-6 h-fit w-72">
                  <div className="flex items-center gap-4">
                    <Image
                      src="/illustrations/img-document-penci-fill.png"
                      width={48}
                      height={48}
                      alt=""
                    />

                    <Text variant="heading-lg">Panduan</Text>
                  </div>

                  <ul className="pl-6 list-disc">
                    <li>
                      <Text>Pengisian akan diberikan hasil pemantauan.</Text>
                    </li>
                    <li>
                      <Text>
                        Refleksikan Upaya Tindak Lanjut yang sudah Anda lakukan.
                      </Text>
                    </li>
                    <li>
                      <Text>
                        Pastikan pengisian Anda relevan dengan realisasi Upaya
                        Tindak Lanjut Anda.
                      </Text>
                    </li>
                  </ul>
                </SimpleCard>

                <SimpleCard className="flex-1 !rounded mb-24">
                  {isSuccess && (
                    <>
                      <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
                        <Text variant="heading-lg">
                          Isi refleksi Anda terhadap upaya yang sudah dilakukan
                        </Text>
                        <Text>
                          Pengisian akan diberikan hasil pemantauan. Pastikan
                          pengisian Anda relevan dengan realisasi Upaya Tindak
                          Lanjut Anda.
                        </Text>
                      </div>
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit(onSubmit)}
                          className="flex flex-col gap-6 p-6"
                        >
                          <SimpleCard className="flex-1 !rounded">
                            <div className="p-4 space-y-2">
                              <div>
                                <Text variant="action">
                                  Realisasi Upaya Tindak Lanjut Anda:
                                </Text>
                              </div>

                              {isUpayaTindakLanjutLoading && (
                                <Shimmer className="!w-full" />
                              )}

                              {upayaTindakLanjutFormStatus === 'success' && (
                                <Text
                                  color="subdued"
                                  dangerouslySetInnerHTML={{
                                    __html: xss(
                                      nl2br(
                                        stripHTMLTags(
                                          parsedUpayaTindakLanjutValues?.realisasi ??
                                            '-'
                                        )
                                      )
                                    ),
                                  }}
                                />
                              )}
                            </div>
                          </SimpleCard>

                          {Object.entries(FORM_LABELS).map(
                            ([key, { title, label }], index) => (
                              <div key={`refleksi-tindak-lanjut-form-${key}`}>
                                <FormField
                                  control={form.control}
                                  name={
                                    key as keyof TPraktikKinerjaRefleksiTindakLanjutFormValue
                                  }
                                  rules={{
                                    required: MSG_ERR_REQUIRED,
                                  }}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        {Boolean(title) && (
                                          <Text
                                            variant="heading-md"
                                            className="mb-1"
                                          >
                                            {title}
                                          </Text>
                                        )}

                                        <Text
                                          variant="body-bold"
                                          className="mb-1"
                                        >
                                          {index + 1}. {label}
                                        </Text>
                                      </FormLabel>

                                      <FormControl>
                                        <Textarea
                                          placeholder="Ketik di sini"
                                          aria-label={label}
                                          {...field}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            )
                          )}

                          <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-end w-full gap-4 px-24 py-4 bg-white border-t border-subdued">
                            <Button
                              color={isSubmitDisabled ? 'blue' : 'white'}
                              size="md"
                              className={clsx(
                                '!px-6',
                                isSubmitDisabled &&
                                  '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
                              )}
                              onClick={() => handleSubmitFormValues(true)}
                              isLoading={submitStatus === 'loading'}
                              disabled={isSubmitDisabled}
                            >
                              Simpan draf
                            </Button>
                            <Button
                              color="blue"
                              size="md"
                              className={clsx(
                                '!px-6',
                                isSubmitDisabled &&
                                  '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
                              )}
                              type="submit"
                              isLoading={submitStatus === 'loading'}
                              disabled={isSubmitDisabled}
                            >
                              Kirim
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </>
                  )}
                </SimpleCard>
              </div>

              <DialogConfirmation
                isSubmitting={submitStatus === 'loading'}
                isOpen={isDialogConfirmationOpen}
                onConfirm={() => {
                  handleSubmitFormValues(false).finally(() => {
                    setIsDialogConfirmationOpen(false)
                  })
                }}
                onBack={() => setIsDialogConfirmationOpen(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
